import { UNTITLED_CONVERSATION } from '@gasbi/shared/common/components/AgentChat';
import { formatRelativeTime } from '@gasbi/shared/common/components/Chat/format';
import type { ConversationListItem } from '@gasbi/shared/common/features/agents';

import { TIME_LABELS } from '@/components/timeLabels';
import { CHAT_APPS, chatAppLabel } from '@/features/agents/chatApps';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Input,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/joy';

type ConversationSidebarProps = {
  items: ConversationListItem[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** The app the list is filtered by; null = every app. */
  app: string | null;
  onAppChange: (app: string | null) => void;
};

const FilterChip = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <Chip
    size='sm'
    variant={selected ? 'soft' : 'plain'}
    color={selected ? 'primary' : 'neutral'}
    onClick={onClick}
    slotProps={{ action: { 'aria-pressed': selected } }}
  >
    {label}
  </Chip>
);

/**
 * The full page's conversations: every chat of the user, from any app's panel or from here (ADR-0004 §2.8), filtered
 * by app on the service (`?app=`) and by title here. A list item carries no app, so the app chip shows only while
 * the list is filtered by one.
 */
export const ConversationSidebar = ({
  items,
  loading,
  selectedId,
  onSelect,
  onCreate,
  search,
  onSearchChange,
  app,
  onAppChange,
}: ConversationSidebarProps) => {
  const appLabel = chatAppLabel(app);
  const query = search.trim().toLowerCase();
  const shown = query
    ? items.filter(item => item.title.toLowerCase().includes(query))
    : items;

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack gap={1.5} sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Stack direction='row' justifyContent='space-between'>
          <Typography level='title-md'>Розмови</Typography>
          <Button size='sm' variant='soft' onClick={onCreate}>
            + Нова
          </Button>
        </Stack>
        <Input
          size='sm'
          value={search}
          onChange={event => onSearchChange(event.target.value)}
          placeholder='Пошук у розмовах'
          startDecorator={<SearchIcon fontSize='small' />}
        />
        <Stack direction='row' flexWrap='wrap' gap={0.5}>
          <FilterChip
            label='Усі додатки'
            selected={app === null}
            onClick={() => onAppChange(null)}
          />
          {CHAT_APPS.map(chatApp => (
            <FilterChip
              key={chatApp.key}
              label={chatApp.label}
              selected={app === chatApp.key}
              onClick={() => onAppChange(chatApp.key)}
            />
          ))}
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading && (
          <Stack alignItems='center' sx={{ py: 4 }}>
            <CircularProgress size='sm' />
          </Stack>
        )}
        {!loading && shown.length === 0 && (
          <Typography
            level='body-sm'
            textColor='neutral.500'
            sx={{ px: 2, py: 4, textAlign: 'center' }}
          >
            Розмов не знайдено
          </Typography>
        )}
        <List sx={{ py: 0, '--ListItem-radius': '0px' }}>
          {shown.map(item => {
            const selected = item.id === selectedId;
            return (
              <ListItem key={item.id}>
                <ListItemButton
                  selected={selected}
                  onClick={() => onSelect(item.id)}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    px: 2,
                    py: 1.25,
                    '&.Mui-selected, &.Mui-selected:hover': {
                      bgcolor: 'primary.50',
                    },
                  }}
                >
                  <Typography
                    level='title-sm'
                    noWrap
                    textColor={selected ? 'primary.500' : 'neutral.800'}
                    sx={{ width: '100%' }}
                  >
                    {item.title || UNTITLED_CONVERSATION}
                  </Typography>
                  <Stack direction='row' alignItems='center' gap={1}>
                    {appLabel && (
                      <Chip size='sm' variant='soft' color='primary'>
                        {appLabel}
                      </Chip>
                    )}
                    <Typography level='body-xs' textColor='neutral.400'>
                      {formatRelativeTime(item.updatedAt, TIME_LABELS)}
                    </Typography>
                  </Stack>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Typography
        level='body-xs'
        textColor='neutral.500'
        sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
      >
        Усі ваші розмови з «Gasbi AI»: з панелі в Supply, CRM та інших додатках.
      </Typography>
    </Stack>
  );
};
